import React, { useEffect, useState } from 'react';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { faTag } from '@fortawesome/pro-light-svg-icons';

import { MONTHS } from '../utils/months';

import { RecentPurchaseDetail } from './RecentPurchaseDetail';

import { monetizeWithDecimal } from 'common/utils/strings';
import { createStyles } from 'utils/createStyle';
import { getBrandsThunk, selectBrandId } from 'common/features/store/duck/brands/duck';
import { useRootSelector } from 'rootStore';
import { Order, OrderLineItem } from 'common/api/e-comm/models/Order';
import { getProduct } from 'common/api/e-comm/products';
import {
    Product,
    ProductVariant,
    ProductType,
    ProductVariantOption,
} from 'common/api/e-comm/models/Product';
import { APIResponse } from 'common/api/models';
import { addItemToCart } from 'common/api/e-comm/cart/lineitem';
import { requestCartItems } from 'common/features/store/duck/cart/duck';
import { useNavMenu } from 'components/nav/context';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { useAlert } from 'modules/alerts';
import { selectIsShipToCoordinatorEnabled } from 'common/features/store/duck/organization/duck';

const styles = createStyles({
    additionalBrandsBadge: [
        'flex items-center justify-center bg-pale-blue rounded-full text-brand-dark font-semibold self-center',
        {
            minHeight: 25,
            minWidth: 25,
            maxHeight: 25,
            maxWidth: 25,
            fontSize: 10,
            lineHeight: '15px',
        },
    ],
    brandContainer: ['md:mb-0', { marginBottom: 10 }],
    container: ['border-solid border-brand border rounded-md flex flex-col text-brand-dark'],
    closeIcon: ['text-xl text-brand-dark cursor-pointer', { marginRight: 20, marginTop: 15 }],
    header: ['justify-between flex flex-row'],
    headerDateContainer: [{ padding: 10 }],
    headerDate: [{ fontSize: 17, lineHeight: '24px', fontWeight: 400 }],
    orderNumber: ['text-grey-1', { fontSize: 12, lineHeight: '14px', fontWeight: 400 }],
    link: [{ fontSize: 12, lineHeight: '16px', fontWeight: 600, textDecoration: 'underline' }],
    summaryBuyAgain: ['flex cursor-pointer items-center', { paddingRight: 20, paddingLeft: 20 }],
    summary: [
        'md:pl-5 md:pr-5 md:pb-5 md:pt-5 pl-3 pr-3 pb-3 pt-3 flex flex-col md:justify-between md:flex-row border-t border-solid border-grey-light border-b ',
    ],
    summaryLabel: ['text-grey-1 block', { fontSize: 10, lineHeight: '15px' }],
    summaryBrandContainer: ['flex flex-row', { maxHeight: 35 }],
    summaryBrandImage: ['max-h-6 md:max-h-9 md:pr-4 md:pl-4 pr-3'],
    totalsContainer: ['flex flex-row', { fontSize: 17, lineHeight: '24px', fontWeight: 400 }],
    totalDivider: ['flex flex-col md:flex-grow justify-between', { marginRight: 50 }],
    footer: ['text-center', { padding: 10, cursor: 'pointer' }],
    localProduct: [
        'text-brand-dark font-bold whitespace-nowrap md:whitespace-normal text-xs sm:text-sm',
    ],
    localProductContainer: ['flex gap-2 items-center h-6 sm:h-9'],
});

export const sumByLineItemMoneyProp = (lineItems: OrderLineItem[], sumBy: string): string => {
    const propSum = lineItems.reduce((sum, lineItem) => {
        const lineItemValue = lineItem[sumBy];

        if (typeof lineItemValue !== 'number') {
            throw new Error(`Cannot sum ${typeof lineItemValue} with number`);
        }

        return sum + lineItemValue;
    }, 0);

    return monetizeWithDecimal(propSum);
};

export const formatISODate = (date: string): string => {
    const jsDate = new Date(date);
    const [year, monthNbr, day] = [jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate()];

    return `${MONTHS[monthNbr]} ${day}, ${year}`;
};

interface RecentPurchaseProps {
    order: Order;
}

interface AdditionalBrandsProps {
    count: number;
}

const AdditionalBrands = ({ count }: AdditionalBrandsProps) => {
    return (
        <div className={classNames(styles.additionalBrandsBadge, count > 0 ? 'block' : 'hidden')}>
            <span>+{count}</span>
        </div>
    );
};

interface ProductsById {
    [key: string]: Product;
}

const computeCanBuyAgain = (lines: OrderLineItem[]) => {
    const linesWithoutReload = lines.filter(
        (line: OrderLineItem) => line.productType !== ProductType.Reload
    );
    return lines.length > 0 && linesWithoutReload.length > 0;
};

const useBuyAgainFlow = (order: Order) => {
    const [canBuyAgain] = useState<boolean>(computeCanBuyAgain(order.lineItems));
    const isShipToCoordinatorEnabled = useSelector(selectIsShipToCoordinatorEnabled);
    const dispatch = useDispatch<any>();
    const [isRunning, setIsRunning] = useState(false);

    const triggerBuyAgain = async () => {
        try {
            setIsRunning(true);

            const lines = order.lineItems.filter(
                (line: OrderLineItem) => line.productType !== ProductType.Reload
            );
            // NOTE: Filter the line-items first to make sure we don't fetch the same product more than once
            const products: ProductsById = await Promise.all(
                lines.map((line: OrderLineItem) => {
                    return getProduct(line.productId);
                })
            )
                .then((response: APIResponse<Product, null>[]): Product[] => {
                    return response
                        .map((productResponse: APIResponse<Product, null>) => productResponse?.data)
                        .filter((p: Product | null | undefined) => Boolean(p)) as Product[];
                })
                .then((products: Product[]) => {
                    return products
                        .filter(({ isAvailable, isVisible }: Product) => isAvailable && isVisible)
                        .reduce((acc: ProductsById, product: Product) => {
                            acc[product.id] = product;
                            return acc;
                        }, {} as ProductsById);
                })
                .catch(() => ({} as ProductsById));

            // NOTE: Because we don't have the __selectedOption__ available in OrderLineItem
            // we need to cross reference the __OrderLineItem.faceValue__ to the product variants.
            // It will allow us to construct the selectedOption

            const addItemsToCart = async () => {
                const result: { product: Product; addedSuccessfully: boolean }[] = [];

                for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];
                    const product = products[line.productId];

                    // skip if product is not found
                    if (!product) {
                        result.push({ product, addedSuccessfully: false });
                        continue;
                    }

                    // skip lineItem if the product cannot be shipped to the user
                    if (
                        product.type === ProductType.GiftCard &&
                        !isShipToCoordinatorEnabled &&
                        !product?.isShipToHomeEligible
                    ) {
                        result.push({ product, addedSuccessfully: false });
                        continue;
                    }

                    const productData = {
                        productId: product.id,
                        selectedOptions: {},
                        quantity: line.quantity,
                        priceOverride: line.faceValue,
                    };

                    // NOTE: Turn this into a reduce instead of FILTER
                    // Step 1: we must cross reference the __line.productId__ with our __products__
                    if (product.options.length > 0) {
                        // NOTE: For Step 2
                        // - We should check __variant__ for product-type GIFT-CARD
                        // - use __line.faceValue__ as the priceOverride when setting up the addToCart for EGIFT-CARD

                        // Step 2: cross reference the __line.faceValue__ against the product variants
                        const variant = product.variants.find((variant: ProductVariant) => {
                            return (
                                variant.price === line.faceValue &&
                                variant.isAvailable &&
                                variant.isVisible
                            );
                        });

                        const optionExists = (variant?.options ?? []).find(
                            (option: ProductVariantOption) => {
                                return option.value === monetizeWithDecimal(line.faceValue);
                            }
                        );

                        // CASE: Variant not found OR if the option no longer exists -- then skip adding to cart
                        if (!variant || !optionExists) {
                            result.push({ product, addedSuccessfully: false });
                            continue;
                        }
                        const option = optionExists as ProductVariantOption;

                        productData.selectedOptions = {
                            [option.optionId]: option.value,
                        };
                        productData.priceOverride = null as unknown as number;
                    }

                    try {
                        const addItemToCartResult = await addItemToCart(productData);
                        result.push({
                            product,
                            addedSuccessfully: Boolean(addItemToCartResult.response?.ok),
                        });
                    } catch {
                        result.push({ product, addedSuccessfully: false });
                    }
                }

                return result;
            };

            const results = await addItemsToCart();

            // NOTE: for each available items we should call the addProductToCart to add it to the cart
            dispatch(requestCartItems());

            return results;
        } finally {
            setIsRunning(false);
        }
    };

    return { triggerBuyAgain, canBuyAgain, isRunning };
};

export const RecentPurchase = ({ order }: RecentPurchaseProps) => {
    const brandsById = useRootSelector((store) => selectBrandId(store));
    const dispatch = useDispatch<any>();
    const [expand, setExpand] = useState(false);
    const { triggerBuyAgain, isRunning } = useBuyAgainFlow(order);
    const alert = useAlert();
    const { setOpenMenu } = useNavMenu();

    useEffect(() => {
        // Only fetch brands that have not already been fetched and aren't in the store
        const brandIds = uniq(
            order.lineItems
                .map((lineItem) => lineItem.brandId)
                .filter((brandId) => !brandsById[brandId] || !brandId)
        );
        dispatch(getBrandsThunk(brandIds));
        // only run this on initial mount
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const getBrandSrcUrl = (brandId: number): string => {
        const brand = brandsById[brandId];
        return brand ? brand.imageUrl : '';
    };

    // We only want to show the first 4 brands in the summary
    const uniqueBrands = uniqBy(order.lineItems, 'brandId');

    const summaryBrands = uniqueBrands.slice(0, 4);
    const remainingBrands = uniqueBrands.length - summaryBrands.length;

    const totalEarnings = sumByLineItemMoneyProp(order.lineItems, 'totalEarnings');

    const onBuyAgain = async () => {
        // NOTE: We require the productId to be in the `lineItem` otherwise we cannot add the item to cart
        // NOTE: Use the `common/api/e-comm/products/index.ts::getProduct` endpoint to fetch product detail
        //       in order to see if the items are available or not.
        // NOTE: Look into `web/src/features/product/ProductDetailScreen.tsx::handleAddItemToCart` as an
        //       example on how to add items to cart.

        const result = await triggerBuyAgain();
        const isEmpty = result.length <= 0;
        const addedSomeItemSuccessfully = result.some((r) => r.addedSuccessfully);
        const hasSomeError = result.some((r) => !r.addedSuccessfully);

        if (hasSomeError || isEmpty) {
            alert({
                title: 'Buy Again Notice',
                message: (
                    <span className="text-grey-1">
                        Some items are not available and cannot be added to your cart at this time.
                    </span>
                ),
                onClose: () => {
                    if (addedSomeItemSuccessfully) {
                        setOpenMenu('cart');
                    }
                },
            });
        } else {
            setOpenMenu('cart');
        }
    };

    return (
        <article className={styles.container}>
            <section className={styles.header}>
                <div className={styles.headerDateContainer}>
                    <h4 className={styles.headerDate}>{formatISODate(order.orderDate)}</h4>
                    <span className={styles.orderNumber}>Reference # {order.orderNumber}</span>
                </div>
                {!isRunning ? (
                    <button
                        className={`order_history_buy_again_button ${classNames(
                            styles.summaryBuyAgain,
                            styles.link,
                            expand ? 'invisible' : 'visible'
                        )}`}
                        onClick={onBuyAgain}
                    >
                        Buy Again
                    </button>
                ) : (
                    <div className={expand ? 'hidden' : ''}>
                        <ProcessingBuyAgain orientation="vertical" />
                    </div>
                )}
                <FontAwesomeIcon
                    icon={faTimes}
                    onClick={() => setExpand(false)}
                    className={classNames(styles.closeIcon, expand ? 'block' : 'hidden')}
                />
            </section>
            <section className={classNames(styles.summary, expand ? 'hidden' : 'block')}>
                <div className={classNames(styles.brandContainer)}>
                    <span className={styles.summaryLabel}>Brands</span>
                    <div className={styles.summaryBrandContainer}>
                        {summaryBrands.map((lineItem) =>
                            lineItem.isLocalProduct ? (
                                <span
                                    key={lineItem.brandId}
                                    className={styles.localProductContainer}
                                >
                                    <FontAwesomeIcon
                                        className={'text-brand'}
                                        icon={faTag}
                                        size="sm"
                                    />
                                    <span className={styles.localProduct}>Local Product</span>
                                </span>
                            ) : (
                                <img
                                    key={lineItem.brandId}
                                    className={styles.summaryBrandImage}
                                    src={getBrandSrcUrl(lineItem.brandId)}
                                />
                            )
                        )}
                        <AdditionalBrands count={remainingBrands} />
                    </div>
                </div>
                <div className={styles.totalsContainer}>
                    <div className={styles.totalDivider}>
                        <span className={styles.summaryLabel}>Total</span>
                        <span className="text-brand-dark">
                            {sumByLineItemMoneyProp(order.lineItems, 'totalValue')}
                        </span>
                    </div>
                    <div className="flex-col flex flex-grow justify-between">
                        <span className={styles.summaryLabel}>Earnings</span>
                        <span className="text-success">{totalEarnings}</span>
                    </div>
                </div>
            </section>
            <section
                onClick={() => setExpand(true)}
                className={classNames(styles.footer, expand ? 'hidden' : 'block')}
            >
                <span className={styles.link}>Click for more details</span>
            </section>
            <RecentPurchaseDetail
                order={order}
                expand={expand}
                setExpand={setExpand}
                getBrandSrcUrl={getBrandSrcUrl}
                onBuyAgain={onBuyAgain}
                isBuyAgainRunning={isRunning}
            />
        </article>
    );
};

export const ProcessingBuyAgain = ({ orientation }: { orientation: 'horizontal' | 'vertical' }) => {
    return (
        <div className="flex items-center px-2 justify-center h-full">
            <div
                className={`flex items-center ${
                    orientation == 'vertical' ? 'flex-col' : 'flex-row gap-2'
                }`}
            >
                <ActivityIndicator width={32} />
                <div className="text-xs text-brand">Adding to cart...</div>
            </div>
        </div>
    );
};
